import { useRef, useState, useEffect } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, Mail, MapPin, Download, ArrowUpRight, Truck, Tractor, ShieldCheck, Clock, Droplets, FileText, Search } from "lucide-react";
import useLenis from "@/hooks/useLenis";
import { setCanonical } from "@/lib/seo";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import Marquee from "@/components/Marquee";
import QuoteForm from "@/components/QuoteForm";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { api, API } from "@/lib/api";

const HERO_IMG =
  "https://images.unsplash.com/photo-1516937941344-00b4e0337589?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwxfHxpbmR1c3RyaWFsJTIwb2lsJTIwcmVmaW5lcnklMjBhcmNoaXRlY3R1cmV8ZW58MHx8fHwxNzg2MzczMDQ1fDA&ixlib=rb-4.1.0&q=85";
const TRUCK_IMG =
  "https://images.unsplash.com/photo-1616432043562-3671ea2e5242?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHwxfHxmcmVpZ2h0JTIwdHJhbnNwb3J0JTIwdHJ1Y2slMjBoaWdod2F5fGVufDB8fHx8MTc4NjM3MzA0NXww&ixlib=rb-4.1.0&q=85";
const TRACTOR_IMG =
  "https://images.unsplash.com/photo-1606739211185-2c846d734a6d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

const ease = [0.22, 1, 0.36, 1];
const reveal = {
  hidden: { opacity: 0, y: 40 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.7, ease, delay: i * 0.08 } }),
};

export default function Home() {
  useLenis();
  useEffect(() => { setCanonical("/"); }, []);
  return (
    <main id="top" className="bg-[var(--bg)] overflow-x-hidden">
      <div className="grain" />
      <Navbar />
      <Hero />
      <Marquee />
      <About />
      <Catalog />
      <Preventivi />
      <AdBlue />
      <Faq />
      <Contatti />
      <News />
      <Footer />
    </main>
  );
}

/* ----------------------------- HERO ----------------------------- */
function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);

  const lines = ["L'ENERGIA", "CHE MUOVE", "IL TUO BUSINESS"];

  return (
    <section ref={ref} className="relative h-[100svh] min-h-[640px] flex items-end overflow-hidden bg-navy">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img src={HERO_IMG} alt="Deposito carburanti Martegiani Petroli Srl – vendita gasolio e lubrificanti a Teramo e provincia" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-navy/75" />
        <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/30 to-navy/60" />
      </motion.div>

      <div className="relative max-w-8xl mx-auto w-full px-6 md:px-12 pb-16 md:pb-24">
        <motion.img
          src="/logo-mp-mark-white.png"
          alt="Logo Martegiani Petroli Srl"
          data-testid="hero-logo-mark"
          initial={{ opacity: 0, scale: 0.85, y: 10 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.8, ease }}
          className="h-20 md:h-28 w-auto mb-6 opacity-90 drop-shadow-[0_6px_18px_rgba(0,0,0,0.35)]"
        />
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.8 }}
          className="text-orange text-xs md:text-sm font-semibold uppercase tracking-[0.35em] mb-6"
          data-testid="hero-eyebrow"
        >
          Gasolio, Lubrificanti & Additivi · Montorio al Vomano (TE)
        </motion.p>

        <h1 data-testid="hero-title" className="font-display text-white leading-[0.92] text-5xl sm:text-7xl lg:text-8xl tracking-tight">
          <span className="sr-only">
            Martegiani Petroli Srl – Vendita gasolio agricolo e autotrazione, lubrificanti Shell, IP, Tamoil e Petronas, consegna carburante a Teramo e provincia
          </span>
          <span aria-hidden="true">
            {lines.map((line, i) => (
              <span key={i} className="block overflow-hidden">
                <motion.span
                  className="block"
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.9, ease, delay: 0.3 + i * 0.12 }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </span>
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.7 }}
          className="mt-8 flex flex-col sm:flex-row sm:items-center gap-6 max-w-3xl"
        >
          <p className="text-white/70 text-base md:text-lg leading-relaxed max-w-md">
            Consegna e rifornimento di gasolio (diesel) a domicilio a Teramo e provincia, per
            autotrazione e agricoltura. Riforniamo serbatoi aziendali e privati; catalogo completo
            di lubrificanti Shell, IP, Tamoil e Petronas e AdBlue Brenntag.
          </p>
          <div className="flex gap-3">
            <a href="#preventivi" data-testid="hero-cta-preventivo" className="bg-orange text-white px-6 py-3.5 text-sm font-semibold uppercase tracking-[0.12em] hover:bg-white hover:text-navy transition-colors">
              Richiedi Preventivo
            </a>
            <a href="#cataloghi" data-testid="hero-cta-cataloghi" className="border border-white/30 text-white px-6 py-3.5 text-sm font-semibold uppercase tracking-[0.12em] hover:bg-white hover:text-navy transition-colors">
              Cataloghi
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

/* ----------------------------- ABOUT ----------------------------- */
const chapters = [
  { n: "01", icon: ShieldCheck, t: "Affidabilità", d: "Un partner solido per la fornitura energetica della tua azienda, con consegne puntuali e prodotti certificati." },
  { n: "02", icon: Droplets, t: "Qualità", d: "Lubrificanti delle migliori marche — Shell, IP e Tamoil — e gasolio conforme agli standard di mercato." },
  { n: "03", icon: Clock, t: "Tempestività", d: "Preventivi rapidi e aggiornati alle quotazioni giornaliere, per decisioni sempre informate." },
];

function About() {
  return (
    <section id="chi-siamo" className="max-w-8xl mx-auto px-6 md:px-12 py-24 md:py-32">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
        <motion.div
          variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-100px" }}
          className="lg:col-span-5"
        >
          <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-orange mb-5">Chi Siamo</p>
          <h2 className="font-display text-navy text-4xl md:text-5xl lg:text-6xl leading-[0.95] tracking-tight">
            Gasolio, lubrificanti ed energia per la tua attività
          </h2>
          <p className="text-ink-soft text-lg leading-relaxed mt-8 max-w-md">
            Martegiani Petroli Srl opera dal 1985 nella distribuzione di carburanti e lubrificanti,
            offrendo un servizio professionale e dedicato ad aziende, agricoltori e operatori
            dell'autotrazione. La nostra sede è a Montorio al Vomano, nel cuore dell'Abruzzo.
          </p>
        </motion.div>

        <div className="lg:col-span-7 lg:pt-4 space-y-px bg-navy/10">
          {chapters.map((c, i) => (
            <motion.div
              key={c.n}
              custom={i} variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }}
              className="bg-[var(--bg)] group flex gap-6 md:gap-10 py-8 items-start"
              data-testid={`chapter-${c.n}`}
            >
              <span className="font-display text-2xl text-orange w-12 shrink-0">{c.n}</span>
              <c.icon className="w-7 h-7 text-navy shrink-0 mt-1" strokeWidth={1.5} />
              <div>
                <h3 className="font-display text-2xl text-navy">{c.t}</h3>
                <p className="text-ink-soft mt-2 leading-relaxed">{c.d}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ----------------------------- CATALOG ----------------------------- */
const brands = [
  { key: "shell", name: "Shell", color: "#DE311A", desc: "Lubrificanti e oli motore Shell per ogni esigenza industriale e automotive.", lubeFinder: "https://www.shell.it/mobilita/oli-motore-e-lubrificanti-shell/lubematch.html" },
  { key: "ip", name: "IP", color: "#004E98", desc: "Gamma completa di oli e lubrificanti IP (Gruppo api) per autotrazione e agricoltura.", lubeFinder: "https://ip.gruppoapi.com/prodotti-e-servizi/prodotti/oli-lubrificanti/" },
  { key: "tamoil", name: "Tamoil", color: "#C8102E", desc: "Prodotti Tamoil selezionati per prestazioni e affidabilità nel tempo.", lubeFinder: "https://www.tamoil.ch/it/i-nostri-lubrificanti/" },
  { key: "petronas", name: "Petronas", color: "#00A19A", desc: "Lubrificanti Petronas ad alte prestazioni per auto, trasporto pesante e industria.", lubeFinder: "https://it.pli-petronas.com/it/applicazioni-petronas" },
];

function Catalog() {
  const [catalogs, setCatalogs] = useState({});

  useEffect(() => {
    api
      .get("/catalogs")
      .then(({ data }) => {
        const map = {};
        data.forEach((c) => (map[c.brand] = c));
        setCatalogs(map);
      })
      .catch(() => {});
  }, []);

  const downloadUrl = (key) => {
    const c = catalogs[key];
    if (c?.custom) return `${API.replace(/\/api$/, "")}${c.url}`;
    return `/catalogs/catalogo-${key}.pdf`;
  };

  return (
    <section id="cataloghi" className="bg-navy py-24 md:py-32">
      <div className="max-w-8xl mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-orange mb-5">Catalogo Olii</p>
            <h2 className="font-display text-white text-4xl md:text-5xl lg:text-6xl leading-[0.95] tracking-tight max-w-xl">
              Cataloghi lubrificanti Shell, IP, Tamoil e Petronas
            </h2>
          </div>
          <p className="text-white/50 max-w-sm md:text-right">
            Distribuzione di lubrificanti Shell, IP e Tamoil in tutto l'Abruzzo. Consulta e scarica
            i cataloghi in PDF, oppure richiedi subito un preventivo dedicato. Le schede tecniche e
            di sicurezza sono disponibili sui siti ufficiali delle marche, tramite il pulsante «Trova l'olio giusto».
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10">
          {brands.map((b, i) => (
            <motion.div
              key={b.key}
              custom={i} variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true }}
              className="group relative bg-navy p-7 md:p-8 min-h-[340px] flex flex-col justify-between hover:bg-[#0f1c3a] transition-colors"
              data-testid={`catalog-card-${b.key}`}
            >
              <div>
                <span className="font-display text-4xl md:text-5xl" style={{ color: b.color }}>
                  {b.name}
                </span>
                <p className="text-white/50 mt-5 leading-relaxed text-sm">{b.desc}</p>
              </div>

              <div className="mt-8 space-y-2.5">
                <a
                  href={b.lubeFinder}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`catalog-lubefinder-${b.key}`}
                  className="w-full flex items-center justify-center gap-2 bg-orange py-3 text-white text-sm font-semibold uppercase tracking-[0.12em] hover:brightness-110 transition-[filter]"
                >
                  <Search className="w-4 h-4" /> Trova l'olio giusto
                </a>

                <a
                  href={downloadUrl(b.key)}
                  download
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`catalog-download-${b.key}`}
                  className="flex items-center justify-between border-t border-white/10 pt-3 text-white text-sm font-semibold uppercase tracking-[0.14em] hover:text-orange transition-colors"
                >
                  <span className="flex items-center gap-2"><Download className="w-4 h-4 text-orange" /> Scarica PDF</span>
                  <ArrowUpRight className="w-5 h-5 text-white/40 group-hover:text-orange transition-colors" />
                </a>

                <OliiQuoteDialog brand={b.name} slug={b.key} color={b.color} />
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function OliiQuoteDialog({ brand, slug, color }) {
  return (
    <Dialog>
      <DialogTrigger asChild>
        <button
          data-testid={`catalog-quote-btn-${slug}`}
          className="w-full flex items-center justify-center gap-2 border border-white/25 py-3 text-white text-sm font-semibold uppercase tracking-[0.12em] hover:bg-orange hover:border-orange transition-colors"
        >
          <FileText className="w-4 h-4" /> Richiedi preventivo
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white" data-testid={`olii-dialog-${slug}`}>
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-navy">
            Preventivo Olii <span style={{ color }}>{brand}</span>
          </DialogTitle>
        </DialogHeader>
        <div className="pt-2">
          <QuoteForm category="olii" tipo={`Olii - ${brand}`} slug={`olii-${slug}`} />
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ----------------------------- PREVENTIVI ----------------------------- */
function Preventivi() {
  return (
    <section id="preventivi" className="max-w-8xl mx-auto px-6 md:px-12 py-24 md:py-32">
      <div className="mb-14 max-w-2xl">
        <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-orange mb-5">Richiesta Preventivo Gasolio</p>
        <h2 className="font-display text-navy text-4xl md:text-5xl lg:text-6xl leading-[0.95] tracking-tight">
          Preventivo gasolio agricolo e autotrazione
        </h2>
        <p className="text-ink-soft text-lg mt-6 leading-relaxed">
          Vendita e consegna di gasolio agricolo e autotrazione a Teramo, con prezzi competitivi
          a Montorio al Vomano e provincia. Seleziona la tipologia di gasolio e compila il modulo:
          il nostro ufficio commerciale ti ricontatterà con un'offerta valida per la data di emissione.
        </p>
      </div>

      <Tabs defaultValue="autotrazione" className="w-full">
        <TabsList className="bg-transparent p-0 h-auto gap-3 mb-8 flex-wrap justify-start" data-testid="preventivi-tabs">
          <TabsTrigger
            value="autotrazione"
            data-testid="tab-autotrazione"
            className="data-[state=active]:bg-navy data-[state=active]:text-white bg-white border border-navy/15 text-navy px-6 py-3 rounded-none text-sm font-semibold uppercase tracking-[0.1em] flex items-center gap-2"
          >
            <Truck className="w-4 h-4" /> Gasolio Autotrazione
          </TabsTrigger>
          <TabsTrigger
            value="agricolo"
            data-testid="tab-agricolo"
            className="data-[state=active]:bg-navy data-[state=active]:text-white bg-white border border-navy/15 text-navy px-6 py-3 rounded-none text-sm font-semibold uppercase tracking-[0.1em] flex items-center gap-2"
          >
            <Tractor className="w-4 h-4" /> Gasolio Agricolo
          </TabsTrigger>
        </TabsList>

        <TabsContent value="autotrazione">
          <QuotePanel img={TRUCK_IMG} tag="Autotrazione" title="Gasolio per Autotrazione" category="gasolio" tipo="Gasolio Autotrazione" slug="autotrazione" desc="Carburante per flotte, mezzi pesanti e trasporto merci." />
        </TabsContent>
        <TabsContent value="agricolo">
          <QuotePanel img={TRACTOR_IMG} tag="Agricoltura" title="Gasolio Agricolo" category="gasolio" tipo="Gasolio Agricolo" slug="agricolo" desc="Gasolio agevolato per macchine agricole e lavorazioni dei campi." />
        </TabsContent>
      </Tabs>
    </section>
  );
}

function QuotePanel({ img, tag, title, desc, category, tipo, slug }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-px bg-navy/10 border border-navy/10">
      <div className="lg:col-span-5 relative min-h-[280px] lg:min-h-full bg-navy overflow-hidden">
        <img src={img} alt={title} loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover opacity-70" />
        <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/40 to-transparent" />
        <div className="relative p-8 md:p-10 h-full flex flex-col justify-end">
          <span className="text-orange text-[11px] font-semibold uppercase tracking-[0.3em]">{tag}</span>
          <h3 className="font-display text-white text-3xl md:text-4xl mt-3 leading-tight">{title}</h3>
          <p className="text-white/70 mt-3 max-w-xs">{desc}</p>
        </div>
      </div>
      <div className="lg:col-span-7 bg-white p-6 md:p-10">
        <QuoteForm category={category} tipo={tipo} slug={slug} />
      </div>
    </div>
  );
}

/* ----------------------------- ADBLUE ----------------------------- */
const ADBLUE_IMG =
  "https://static.prod-images.emergentagent.com/jobs/d38c18a7-43db-431c-8869-80b3fcc5d25a/images/719408d24025adcd6767d3be8dd5bc0d4962fdedfe9ac290d61512f62addf072.jpeg";

const ADBLUE_OPTIONS = [
  "AdBlue - Cubo 1000 litri (Brenntag)",
  "AdBlue - Sfuso minimo 5000 litri (Brenntag)",
  "AdBlue - Taniche (Brenntag)",
  "AdBlue - Rifornimento cubo già fornito",
];

function AdBlue() {
  return (
    <section id="adblue" className="bg-navy py-24 md:py-32">
      <div className="max-w-8xl mx-auto px-6 md:px-12">
        <div className="mb-14 max-w-2xl">
          <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-orange mb-5">AdBlue · Brenntag</p>
          <h2 className="font-display text-white text-4xl md:text-5xl lg:text-6xl leading-[0.95] tracking-tight">
            Preventivo AdBlue Brenntag
          </h2>
          <p className="text-white/60 text-lg mt-6 leading-relaxed">
            Fornitura di AdBlue a marchio Brenntag: cubi da 1000 litri, taniche e rifornimento
            dei cubi già forniti da noi. Seleziona la modalità e richiedi il tuo preventivo.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-px bg-white/10 border border-white/10">
          <div className="lg:col-span-5 relative min-h-[300px] lg:min-h-full bg-navy overflow-hidden">
            <img src={ADBLUE_IMG} alt="Cubo AdBlue 1000 litri e taniche Brenntag" loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover opacity-80" />
            <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/40 to-transparent" />
            <div className="relative p-8 md:p-10 h-full flex flex-col justify-end">
              <span className="text-orange text-[11px] font-semibold uppercase tracking-[0.3em]">Brenntag</span>
              <h3 className="font-display text-white text-3xl md:text-4xl mt-3 leading-tight">AdBlue certificato</h3>
              <p className="text-white/70 mt-3 max-w-xs">Soluzione per la riduzione delle emissioni dei motori diesel, per mezzi leggeri, pesanti e agricoli.</p>
            </div>
          </div>
          <div className="lg:col-span-7 bg-white p-6 md:p-10">
            <QuoteForm category="adblue" slug="adblue" options={ADBLUE_OPTIONS} />
          </div>
        </div>
      </div>
    </section>
  );
}

/* ----------------------------- NEWS ----------------------------- */
function News() {
  const [news, setNews] = useState(null);

  useEffect(() => {
    api.get("/news").then(({ data }) => setNews(data)).catch(() => setNews([]));
  }, []);

  if (!news || news.length === 0) return null; // fallback: si nasconde senza errori

  const fmtDate = (iso) => {
    if (!iso) return "";
    try {
      return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" });
    } catch {
      return "";
    }
  };

  return (
    <section id="news" className="bg-[var(--bg)] py-24 md:py-32" data-testid="news-section">
      <div className="max-w-8xl mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-orange mb-5">Aggiornamenti</p>
            <h2 className="font-display text-navy text-4xl md:text-5xl lg:text-6xl leading-[0.95] tracking-tight">
              News dal settore petrolio
            </h2>
          </div>
          <p className="text-ink-soft max-w-sm md:text-right">
            Le ultime notizie dal mondo dell'energia e dei carburanti, da fonti autorevoli.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-navy/10 border border-navy/10">
          {news.map((n, i) => (
            <motion.a
              key={i}
              href={n.link}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`news-item-${i}`}
              custom={i} variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true }}
              className="group bg-white p-6 md:p-7 flex flex-col min-h-[240px] hover:bg-navy transition-colors duration-300"
            >
              <div className="flex items-center justify-between mb-4">
                <span className="text-[10px] font-semibold uppercase tracking-[0.15em] text-orange">{n.source}</span>
                <span className="text-[11px] text-ink-soft group-hover:text-white/50 transition-colors">{fmtDate(n.published)}</span>
              </div>
              <h3 className="font-cab font-medium text-lg text-navy leading-snug line-clamp-2 group-hover:text-white transition-colors">
                {n.title}
              </h3>
              <p className="text-sm text-ink-soft mt-3 leading-relaxed line-clamp-2 group-hover:text-white/60 transition-colors">
                {n.summary}
              </p>
              <span className="mt-auto pt-4 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.12em] text-navy group-hover:text-orange transition-colors">
                Leggi <ArrowUpRight className="w-4 h-4" />
              </span>
            </motion.a>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ----------------------------- FAQ ----------------------------- */
const FAQS = [
  { q: "Fate la consegna di gasolio a domicilio a Teramo e provincia?", a: "Sì. Martegiani Petroli Srl effettua consegna e rifornimento di gasolio a domicilio a Teramo, Montorio al Vomano e in tutti i comuni della provincia di Teramo, per autotrazione e agricoltura." },
  { q: "Come funziona il rifornimento del serbatoio aziendale o agricolo?", a: "Rechiamo il gasolio direttamente presso la tua sede e riforniamo il serbatoio aziendale o agricolo. Basta richiedere un preventivo indicando quantità e località del serbatoio da rifornire." },
  { q: "Consegnate anche diesel e AdBlue a domicilio?", a: "Sì, consegniamo gasolio (diesel) per autotrazione e agricolo e AdBlue Brenntag (cubi da 1000 litri, taniche, sfuso e rifornimento cubi), con consegna in provincia di Teramo e dintorni." },
  { q: "Come richiedo un preventivo per il gasolio?", a: "Puoi richiedere un preventivo gratuito dal sito compilando il modulo per gasolio autotrazione o agricolo, oppure contattarci al numero 0861-598527. I prezzi sono aggiornati alle quotazioni di mercato." },
];

function Faq() {
  return (
    <section id="faq" className="max-w-8xl mx-auto px-6 md:px-12 py-24 md:py-32">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-4">
          <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-orange mb-5">FAQ</p>
          <h2 className="font-display text-navy text-4xl md:text-5xl lg:text-6xl leading-[0.95] tracking-tight">
            Consegna gasolio a domicilio a Teramo: domande frequenti
          </h2>
        </div>
        <div className="lg:col-span-8 space-y-px bg-navy/10 border border-navy/10">
          {FAQS.map((f, i) => (
            <motion.div
              key={i}
              custom={i} variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }}
              className="bg-white p-6 md:p-8"
              data-testid={`faq-${i}`}
            >
              <h3 className="font-cab font-medium text-lg md:text-xl text-navy">{f.q}</h3>
              <p className="text-ink-soft mt-2 leading-relaxed">{f.a}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ----------------------------- CONTATTI ----------------------------- */
const contacts = [
  { icon: Phone, label: "Telefono", value: "0861-598527", href: "tel:0861598527", testid: "contact-phone" },
  { icon: Mail, label: "Email", value: "info@martegianipetroli.it", href: "mailto:info@martegianipetroli.it", testid: "contact-email" },
  { icon: MapPin, label: "Sede", value: "Viale Luigi Settembrini 63, Montorio al Vomano (TE) 64046", href: "https://maps.google.com/?q=Viale+Luigi+Settembrini+63+Montorio+al+Vomano", testid: "contact-address" },
  { icon: Clock, label: "Orari uffici", value: "Lun–Ven 07:00–12:30 · 15:30–18:30", testid: "contact-hours" },
];

function Contatti() {
  return (
    <section id="contatti" className="bg-[#eef1f5] py-24 md:py-32">
      <div className="max-w-8xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <div className="lg:col-span-5">
            <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-orange mb-5">Contatti</p>
            <h2 className="font-display text-navy text-4xl md:text-5xl lg:text-6xl leading-[0.95] tracking-tight">
              Consegna carburante a Teramo, provincia e dintorni
            </h2>
            <p className="text-ink-soft text-lg mt-6 leading-relaxed max-w-sm">
              Contattaci per informazioni su forniture, cataloghi e preventivi personalizzati.
            </p>
          </div>

          <div className="lg:col-span-7 flex flex-col gap-4">
            {contacts.map((c, i) => (
              <motion.a
                key={c.label}
                href={c.href}
                target={c.label === "Sede" ? "_blank" : undefined}
                rel="noopener noreferrer"
                data-testid={c.testid}
                custom={i} variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true }}
                className="bg-white border border-navy/10 p-6 md:p-7 flex items-center gap-5 hover:border-orange hover:-translate-y-0.5 transition-[transform,border-color] duration-300 group"
              >
                <span className="w-12 h-12 shrink-0 grid place-items-center bg-navy/5 group-hover:bg-orange/10 transition-colors">
                  <c.icon className="w-6 h-6 text-orange" strokeWidth={1.5} />
                </span>
                <div className="min-w-0">
                  <p className="text-[11px] uppercase tracking-[0.2em] text-ink-soft">{c.label}</p>
                  <p className="font-cab font-medium text-lg md:text-xl text-navy mt-1 leading-snug break-words">{c.value}</p>
                </div>
              </motion.a>
            ))}
          </div>
        </div>

        <motion.div
          variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true }}
          className="mt-12 border border-navy/10 bg-white overflow-hidden"
          data-testid="map-container"
        >
          <iframe
            title="Sede Martegiani Petroli Srl"
            src="https://maps.google.com/maps?q=Viale%20Luigi%20Settembrini%2063%20Montorio%20al%20Vomano%20TE%2064046&t=&z=15&ie=UTF8&iwloc=&output=embed"
            width="100%"
            height="420"
            style={{ border: 0, display: "block", filter: "grayscale(0.2) contrast(1.05)" }}
            allowFullScreen=""
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </motion.div>
      </div>
    </section>
  );
}
